import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SoundProvider } from './contexts/SoundContext';
import { ToastProvider } from './contexts/ToastContext';
import { Navigation } from './components/Navigation';
import { ProtectedRoute } from './components/ProtectedRoute';
import { WordPressPlugin } from './pages/WordPressPlugin';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { PluginCreatorDashboard } from './pages/PluginCreatorDashboard';
import { SiteOwnerDashboard } from './pages/SiteOwnerDashboard';
import LicenseManagement from './pages/LicenseManagement';
import RevenueAnalytics from './pages/RevenueAnalytics';
import AuditLogs from './pages/AuditLogs';
import { InstallationsManagement } from './pages/InstallationsManagement';
import { Pricing } from './pages/Pricing';
import { SiteOwnerPayment } from './pages/SiteOwnerPayment';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { PaymentCancelled } from './pages/PaymentCancelled';

function DashboardRouter() {
  const { profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return <Navigate to="/login" replace />;
  }

  if (profile.role === 'plugin_creator') {
    return <Navigate to="/creator-dashboard" replace />;
  }

  return <Navigate to="/my-sites" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <SoundProvider>
        <ToastProvider>
          <AuthProvider>
            <div className="min-h-screen bg-gray-50">
              <Navigation />
              <Routes>
                <Route path="/" element={<WordPressPlugin />} />
                <Route path="/wordpress-plugin" element={<WordPressPlugin />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/site-owner-payment" element={<SiteOwnerPayment />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                <Route path="/site-owner-dashboard" element={<SiteOwnerDashboard />} />

                <Route
                  path="/dashboard"
                  element={<DashboardRouter />}
                />

                <Route
                  path="/creator-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['plugin_creator']}>
                      <PluginCreatorDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/my-sites"
                  element={
                    <ProtectedRoute allowedRoles={['site_owner']}>
                      <SiteOwnerDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/licenses"
                  element={
                    <ProtectedRoute allowedRoles={['plugin_creator']}>
                      <LicenseManagement />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute allowedRoles={['plugin_creator']}>
                      <RevenueAnalytics />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/audit-logs"
                  element={
                    <ProtectedRoute allowedRoles={['plugin_creator']}>
                      <AuditLogs />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/installations"
                  element={
                    <ProtectedRoute allowedRoles={['plugin_creator']}>
                      <InstallationsManagement />
                    </ProtectedRoute>
                  }
                />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </AuthProvider>
        </ToastProvider>
      </SoundProvider>
    </BrowserRouter>
  );
}

export default App;
